import { useState } from "react";
import { useForm } from "react-hook-form";
import { ArrowUpRight, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import Reveal from "./Reveal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FinalCTA = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: data.name,
        email: data.email,
        whatsapp: data.whatsapp,
        message: data.message || null,
      });
      setSubmitted(true);
      toast.success("Mensagem enviada! Vamos te responder em até 24h.");
      reset();
    } catch (err) {
      toast.error("Não foi possível enviar. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contato"
      className="relative py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-10"
      data-testid="final-cta-section"
    >
      <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#0a0a0a] to-[#050505] overflow-hidden p-8 sm:p-14 lg:p-16">
        <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-[#00D2D3]/15 blur-[120px] pointer-events-none" />

        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <Reveal>
            <h2 className="font-outfit font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05]">
              Vamos <span className="text-gradient-teal">construir.</span>
            </h2>
            <p className="mt-6 text-white/60 font-manrope text-base sm:text-lg max-w-md leading-relaxed">
              Sua ideia merece execução que gera resultado de verdade.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                className="bg-[#00D2D3] text-black hover:bg-[#00D2D3]/90 rounded-full px-7 h-12 font-manrope font-semibold transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,210,211,0.5)] group"
                data-testid="final-cta-form-button"
              >
                <a href="#contato">
                  Iniciar Projeto
                  <ArrowUpRight className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </Button>
              <a
                href="#cases"
                className="text-sm font-manrope font-medium text-white/80 hover:text-white border border-white/15 hover:border-[#00D2D3]/50 rounded-full px-6 h-12 flex items-center transition-all duration-300"
                data-testid="final-cta-view-cases-link"
              >
                Ver Cases
              </a>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-xs font-mono-accent text-white/40 uppercase tracking-wide">
              <span>Resposta em até 24h</span>
              <span>&middot;</span>
              <span>Orçamento gratuito</span>
              <span>&middot;</span>
              <span>Sem compromisso</span>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div
              className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-6 sm:p-8"
              data-testid="lead-form-card"
            >
              {submitted ? (
                <div
                  className="flex flex-col items-center justify-center text-center py-10"
                  data-testid="lead-form-success"
                >
                  <CheckCircle2 className="h-10 w-10 text-[#00D2D3] mb-4" />
                  <p className="font-outfit text-xl text-white font-semibold">
                    Recebemos sua mensagem!
                  </p>
                  <p className="text-white/50 font-manrope text-sm mt-2">
                    Vamos te responder em até 24h.
                  </p>
                  <Button
                    variant="ghost"
                    className="mt-6 text-[#00D2D3] hover:text-[#00D2D3] hover:bg-[#00D2D3]/10"
                    onClick={() => setSubmitted(false)}
                    data-testid="lead-form-send-another-button"
                  >
                    Enviar outra mensagem
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="flex flex-col gap-5"
                  data-testid="lead-capture-form"
                >
                  <h3 className="font-outfit font-semibold text-lg text-white mb-1">
                    Conte um pouco sobre o seu projeto
                  </h3>
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-white/70 text-sm">
                      Nome
                    </Label>
                    <Input
                      id="name"
                      placeholder="Seu nome completo"
                      className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#00D2D3]"
                      data-testid="lead-form-name-input"
                      {...register("name", { required: true })}
                    />
                    {errors.name && (
                      <p
                        className="text-xs text-red-400"
                        data-testid="lead-form-name-error"
                      >
                        Informe seu nome.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-white/70 text-sm">
                      E-mail
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com"
                      className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#00D2D3]"
                      data-testid="lead-form-email-input"
                      {...register("email", { required: true })}
                    />
                    {errors.email && (
                      <p
                        className="text-xs text-red-400"
                        data-testid="lead-form-email-error"
                      >
                        Informe um e-mail válido.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp" className="text-white/70 text-sm">
                      WhatsApp
                    </Label>
                    <Input
                      id="whatsapp"
                      placeholder="(11) 99999-9999"
                      className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#00D2D3]"
                      data-testid="lead-form-whatsapp-input"
                      {...register("whatsapp", { required: true })}
                    />
                    {errors.whatsapp && (
                      <p
                        className="text-xs text-red-400"
                        data-testid="lead-form-whatsapp-error"
                      >
                        Informe seu WhatsApp.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-white/70 text-sm">
                      Sobre o seu projeto
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Conte rapidamente o que você precisa..."
                      className="bg-black/30 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#00D2D3] min-h-[100px]"
                      data-testid="lead-form-message-input"
                      {...register("message")}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-[#00D2D3] text-black hover:bg-[#00D2D3]/90 rounded-full h-11 font-manrope font-semibold mt-2 transition-all duration-300 hover:shadow-[0_0_25px_rgba(0,210,211,0.5)]"
                    data-testid="lead-form-submit-button"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Enviar mensagem"
                    )}
                  </Button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
